/**
 * Создаёт типизированный реестр записей с ключом.
 *
 * Пара к {@link createSlot}, и выбор между ними — про смысл, а не про форму:
 *
 * - **слот** — это место в конкретном компоненте: «вставь сюда». Обработчик
 *   зовут при рендере, он получает контекст этого места. Записи безымянны;
 *   два одинаковых обработчика молча сложатся в массив;
 * - **реестр** — это сущность, которая существует в системе независимо от
 *   места: отчёт, область уведомлений, вид документа. У неё есть код, по
 *   которому её можно найти, перезаписать и прочитать откуда угодно.
 *
 * Регистрация — upsert по коду, как в реестрах платформы: при горячей
 * перезагрузке модуль исполняется повторно, и падение на «уже зарегистрировано»
 * ломало бы dev-режим. Побочный эффект приятный: дубля записи не бывает —
 * в отличие от слота, где второй обработчик тихо добавляется к первому.
 *
 * @example
 * ```ts
 * // Модуль объявляет реестр и его тип
 * export interface ReportLink { code: string; label: string; url: string; order?: number }
 * export const ReportsRegistry = createRegistry<ReportLink>('reports');
 *
 * // Кто угодно регистрирует свою запись в bootstrap
 * ReportsRegistry.register({ code: 'my_report', label: 'Мой отчёт', url: '/my/report' });
 *
 * // Компонент читает всё разом
 * const links = ReportsRegistry.getAll();
 * ```
 */
export interface RegistryEntry {
  /** Код записи — по нему ищут и перезаписывают. */
  code: string;
}

export interface Registry<T extends RegistryEntry> {
  /** Регистрирует запись (upsert по `code`). Возвращает её же — удобно для цепочек. */
  register(entry: T): T;
  /** Запись по коду; `null` — такой не регистрировали. */
  get(code: string): T | null;
  /** Все записи в порядке регистрации. */
  getAll(): T[];
  /** Имя реестра — для сообщений об ошибках у потребителей. */
  readonly name: string;
}

export function createRegistry<T extends RegistryEntry>(name: string): Registry<T> {
  const entries = new Map<string, T>();

  return {
    name,
    register(entry: T): T {
      entries.set(entry.code, entry);
      return entry;
    },
    get(code: string): T | null {
      return entries.get(code) ?? null;
    },
    getAll(): T[] {
      return [...entries.values()];
    },
  };
}
